/**
 * Creator Incentive — creators submit published YouTube videos/shorts and
 * automatically earn ₹150 per video / ₹50 per Short.
 *
 * Route: /creator/incentive
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Youtube,
  Trophy,
  IndianRupee,
  Video,
  Zap,
  Send,
  MessageCircle,
  Calendar,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/creator-incentive`;

export default function CreatorIncentivePage() {
  const [config, setConfig] = useState({ rates: { short: 50, video: 150 }, categories: [] });
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState({ whatsapp: "", display_name: "" });
  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // Submit form state
  const [videoUrl, setVideoUrl] = useState("");
  const [category, setCategory] = useState("Other");
  const [typeOverride, setTypeOverride] = useState("");

  const refresh = async () => {
    try {
      const [cfg, st, pr] = await Promise.all([
        axios.get(`${API}/config`),
        axios.get(`${API}/my-stats`, { withCredentials: true }),
        axios.get(`${API}/profile`, { withCredentials: true }),
      ]);
      setConfig(cfg.data);
      setStats(st.data);
      setProfile(pr.data);
      if (!category && cfg.data.categories?.length) setCategory(cfg.data.categories[0]);
    } catch (err) {
      if (err.response?.status === 401) toast.error("Please log in to view your incentive dashboard");
      else toast.error("Failed to load incentive data");
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const submitVideo = async (e) => {
    e.preventDefault();
    if (!videoUrl.trim() || loading) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/submit`,
        {
          video_url: videoUrl.trim(),
          category,
          ...(typeOverride ? { type_override: typeOverride } : {}),
        },
        { withCredentials: true },
      );
      const s = res.data.submission;
      toast.success(`+₹${s.amount} earned! "${s.title?.slice(0, 40)}…" counted as ${s.type}`);
      setVideoUrl("");
      setTypeOverride("");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    if (!profile.whatsapp?.trim()) return toast.error("WhatsApp number required");
    setSavingProfile(true);
    try {
      await axios.post(`${API}/profile`, profile, { withCredentials: true });
      toast.success("WhatsApp saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingProfile(false);
    }
  };

  const totals = stats?.totals || { earnings: 0, shorts: 0, videos: 0, submissions: 0 };
  const thisMonth = stats?.this_month || { shorts: 0, videos: 0, amount: 0 };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-16">
      <PremiumNav />
      {/* Hero */}
      <section className="border-b border-neutral-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <p className="text-xs uppercase tracking-widest text-[#A855F7] font-semibold mb-3">
            Creator Program · Get paid for what you post
          </p>
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#111111] tracking-tight">
            Your videos earn — automatically.
          </h1>
          <p className="text-base text-neutral-600 mt-3 max-w-2xl">
            Publish on YouTube, paste the link here, and we credit
            <strong> ₹{config.rates?.video || 150}</strong> per full video &
            <strong> ₹{config.rates?.short || 50}</strong> per Short. Monthly totals tracked, admin verifies, payouts made.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
        {/* LEFT — submit + submissions */}
        <div className="space-y-6">
          <Card className="p-6 border-neutral-200" data-testid="incentive-submit-card">
            <div className="flex items-center gap-2 mb-4">
              <Send className="w-4 h-4 text-[#A855F7]" />
              <h2 className="text-lg font-semibold text-[#111111]">Submit a published video</h2>
            </div>
            <form onSubmit={submitVideo} className="space-y-4">
              <div>
                <label className="text-xs text-neutral-500 mb-1 block">YouTube URL (video or short)</label>
                <div className="relative">
                  <Youtube className="w-5 h-5 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2" />
                  <Input
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://youtube.com/watch?v=… or https://youtube.com/shorts/…"
                    className="pl-12 h-11 bg-white border-neutral-300"
                    data-testid="incentive-url-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-neutral-500 mb-1 block">Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm bg-white h-11"
                    data-testid="incentive-category-select"
                  >
                    {(config.categories || []).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-neutral-500 mb-1 block">
                    Type <span className="text-neutral-400">(auto-detected)</span>
                  </label>
                  <select
                    value={typeOverride}
                    onChange={(e) => setTypeOverride(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm bg-white h-11"
                    data-testid="incentive-type-select"
                  >
                    <option value="">Auto-detect from URL</option>
                    <option value="video">Full video (₹{config.rates?.video || 150})</option>
                    <option value="short">Short (₹{config.rates?.short || 50})</option>
                  </select>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading || !videoUrl.trim()}
                className="h-11 px-6 bg-[#A855F7] hover:bg-[#9333EA] text-white w-full sm:w-auto"
                data-testid="incentive-submit-btn"
              >
                {loading ? <><Sparkles className="w-4 h-4 mr-2 animate-spin" /> Verifying…</> : <><Send className="w-4 h-4 mr-2" /> Submit & Earn</>}
              </Button>
            </form>
          </Card>

          {/* Submissions history */}
          <Card className="p-6 border-neutral-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-500" />
                <h2 className="text-lg font-semibold text-[#111111]">Your submissions</h2>
              </div>
              <Badge variant="secondary">{stats?.submissions?.length || 0}</Badge>
            </div>
            {(!stats?.submissions || stats.submissions.length === 0) && (
              <p className="text-sm text-neutral-500 py-8 text-center">
                No submissions yet — paste your first published video URL above ☝️
              </p>
            )}
            {stats?.submissions?.length > 0 && (
              <div className="divide-y divide-neutral-100" data-testid="incentive-submissions-list">
                {stats.submissions.map((s) => (
                  <div key={`${s.video_id}-${s.submitted_at}`} className="py-3 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.type === "short" ? "bg-fuchsia-50 text-fuchsia-600" : "bg-red-50 text-red-600"}`}>
                      {s.type === "short" ? <Zap className="w-5 h-5" /> : <Video className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <a
                        href={s.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-[#111111] hover:text-[#A855F7] truncate block"
                      >
                        {s.title}
                      </a>
                      <p className="text-[11px] text-neutral-500">
                        {s.category} · {s.month} · <span className="uppercase">{s.status}</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-[#111111]">₹{s.amount}</p>
                      <p className="text-[10px] text-neutral-400">{s.type}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* RIGHT — earnings + profile */}
        <aside className="space-y-6">
          <Card className="p-6 border-neutral-200 bg-gradient-to-br from-[#A855F7] to-[#7C3AED] text-white" data-testid="incentive-totals-card">
            <p className="text-xs uppercase tracking-widest opacity-80">Total earnings</p>
            <div className="flex items-baseline mt-2">
              <IndianRupee className="w-6 h-6 opacity-90" />
              <span className="text-5xl font-semibold">{totals.earnings.toLocaleString("en-IN")}</span>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
              <div>
                <p className="opacity-80 text-xs">Videos</p>
                <p className="text-2xl font-semibold">{totals.videos}</p>
              </div>
              <div>
                <p className="opacity-80 text-xs">Shorts</p>
                <p className="text-2xl font-semibold">{totals.shorts}</p>
              </div>
            </div>
          </Card>

          <Card className="p-5 border-neutral-200">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-[#A855F7]" />
              <h3 className="text-sm font-semibold text-[#111111]">This month · {thisMonth.month}</h3>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-neutral-50 rounded-lg py-3">
                <p className="text-[11px] text-neutral-500">Videos</p>
                <p className="text-xl font-semibold text-[#111111]">{thisMonth.videos}</p>
              </div>
              <div className="bg-neutral-50 rounded-lg py-3">
                <p className="text-[11px] text-neutral-500">Shorts</p>
                <p className="text-xl font-semibold text-[#111111]">{thisMonth.shorts}</p>
              </div>
              <div className="bg-neutral-50 rounded-lg py-3">
                <p className="text-[11px] text-neutral-500">Earned</p>
                <p className="text-xl font-semibold text-[#A855F7]">₹{thisMonth.amount}</p>
              </div>
            </div>
          </Card>

          <Card className="p-5 border-neutral-200" data-testid="incentive-profile-card">
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle className="w-4 h-4 text-emerald-500" />
              <h3 className="text-sm font-semibold text-[#111111]">Your WhatsApp</h3>
            </div>
            <p className="text-xs text-neutral-500 mb-3">
              Admin will reach out here for topic suggestions & payout confirmations.
            </p>
            <Input
              value={profile.whatsapp || ""}
              onChange={(e) => setProfile({ ...profile, whatsapp: e.target.value })}
              placeholder="+91XXXXXXXXXX"
              className="h-10 bg-white border-neutral-300"
              data-testid="incentive-whatsapp-input"
            />
            <Input
              value={profile.display_name || ""}
              onChange={(e) => setProfile({ ...profile, display_name: e.target.value })}
              placeholder="Your name (optional)"
              className="h-10 bg-white border-neutral-300 mt-2"
              data-testid="incentive-name-input"
            />
            <Button
              onClick={saveProfile}
              disabled={savingProfile}
              className="w-full mt-3 bg-[#111] hover:bg-black text-white h-10"
              data-testid="incentive-profile-save-btn"
            >
              {savingProfile ? "Saving…" : (<><CheckCircle2 className="w-4 h-4 mr-2" /> Save WhatsApp</>)}
            </Button>
          </Card>

          {stats?.monthly?.length > 0 && (
            <Card className="p-5 border-neutral-200">
              <h3 className="text-sm font-semibold text-[#111111] mb-3">Monthly history</h3>
              <div className="space-y-2">
                {stats.monthly.map((m) => (
                  <div key={m.month} className="flex items-center justify-between text-sm">
                    <span className="text-neutral-600">{m.month}</span>
                    <span className="text-neutral-400 text-xs">{m.videos}v · {m.shorts}s</span>
                    <span className="font-semibold text-[#111111]">₹{m.amount}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </aside>
      </section>
      <PremiumFooter />
    </div>
  );
}
